import React, { ReactNode } from 'react';
import { Flex } from 'glints-aries/es';
import {
  OutlineButton,
  PrimaryButton,
  Typography,
} from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';

import { EmployerPurchaseProductType } from '../../../../common/interfaces/entities';
import { ROUTES } from '../../../../common/routes';
import { UnverifiedCompanyProductUsageWarningBanner } from '../../SharedComponents/UnverifiedCompanyProductUsageWarningBanner';
import { CreditProductPurchaseOrderForAfterPayment } from '../types';
import {
  StyledButtonGroup,
  SuccessModalIntro,
  SuccessModalSummary,
} from './styles.sc';
import { TermsAgreementText } from './TermsAgreementModal';

type ComponentTypeProps = {
  onClose: () => void;
  creditProductPurchaseOrder: CreditProductPurchaseOrderForAfterPayment;
};

function ModalContent({ creditProductPurchaseOrder }: ComponentTypeProps) {
  const { formatNumber, formatDate } = useIntl();
  return (
    <Flex flexDirection="column" gap={'20px'}>
      <SuccessModalIntro>
        <img
          src="images/contact-packs/contact-pack-icon.svg"
          style={{
            width: '72px',
            height: '72px',
          }}
        />
        <SuccessModalSummary>
          <Typography variant="body2">
            <FormattedMessage
              id="text-contact-balance-increased"
              defaultMessage="Your Contact Balance Has Been Increased to {count}"
              values={{
                count: formatNumber(
                  creditProductPurchaseOrder.companyProductInventory?.amount ||
                    0
                ),
              }}
            />
          </Typography>
          <Typography variant="subtitle2" color={Neutral.B40}>
            <FormattedMessage
              id="text-contact-balance-expiry"
              defaultMessage="Your balance will be valid till <bold>{date}</bold>."
              values={{
                date: creditProductPurchaseOrder.companyProductInventory
                  ?.expiryDateTime
                  ? formatDate(
                      creditProductPurchaseOrder.companyProductInventory
                        .expiryDateTime,
                      {
                        year: 'numeric',
                        month: 'short',
                        day: '2-digit',
                      }
                    )
                  : '-',
                bold: (...chunks: ReactNode[]) => (
                  <Typography as="span" variant="caption">
                    {chunks}
                  </Typography>
                ),
              }}
            />
          </Typography>
        </SuccessModalSummary>
      </SuccessModalIntro>
      <UnverifiedCompanyProductUsageWarningBanner />
      <TermsAgreementText />
    </Flex>
  );
}

function ModalTitle() {
  return (
    <FormattedMessage
      id="text-purchased-successfully"
      defaultMessage="Purchased Successfully"
    />
  );
}

function ModalFooter({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  const handleViewContactBalance = () => {
    onClose();
    navigate(
      `/${ROUTES.features}?tab=PURCHASES&purchasedTab=${EmployerPurchaseProductType.SHORTLIST}`
    );
  };

  return (
    <StyledButtonGroup>
      <OutlineButton onClick={handleViewContactBalance}>
        <Typography variant="button">
          <FormattedMessage
            id="interactive-view-contact-balance"
            defaultMessage="View Contact Balance"
          />
        </Typography>
      </OutlineButton>
      <PrimaryButton onClick={onClose}>
        <Typography variant="button">
          <FormattedMessage
            id="interactive-continue"
            defaultMessage="Continue"
          />
        </Typography>
      </PrimaryButton>
    </StyledButtonGroup>
  );
}

export default Object.assign(ModalContent, {
  ModalTitle,
  ModalFooter,
});
